import { useState, useEffect } from "react";
import Button from "@/components/Button";
import { useGlobalToast } from "../contexts/ToastContext";
import {
  Users,
  UserPlus,
  Edit2,
  Trash2,
  Key,
  Lock,
  Loader2,
  AlertCircle,
  Mail,
  Phone,
  Shield,
  ShieldCheck,
  User,
  Send,
  Clock,
} from "lucide-react";
import apiClient from "@/lib/api-client";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import {
  PERMISSIONS,
  PERMISSION_GROUPS,
  PERMISSION_PRESETS,
  ALL_PERMISSION_KEYS,
  type PermissionKey,
  type PermissionGroupKey,
} from "@/lib/permissions";

interface TeamUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  role: string;
  isActive: boolean;
  emailVerified: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  /** Set efectivo de permisos (owner/admin llegan con las 16 claves). */
  permissions?: PermissionKey[];
}

interface TeamLimits {
  currentUsers: number;
  maxUsers: number;
}

/** Catálogo de permisos agrupado (para pintar el editor), calculado una sola
 * vez fuera del componente — mismo patrón de "arrays fuera del componente"
 * usado en otras páginas del dashboard. */
const GROUPED_PERMISSIONS: {
  key: PermissionGroupKey;
  label: string;
  permissions: { key: PermissionKey; label: string }[];
}[] = (Object.keys(PERMISSION_GROUPS) as PermissionGroupKey[]).map(
  (groupKey) => ({
    key: groupKey,
    label: PERMISSION_GROUPS[groupKey],
    permissions: ALL_PERMISSION_KEYS.filter(
      (key) => PERMISSIONS[key].group === groupKey,
    ).map((key) => ({ key, label: PERMISSIONS[key].label })),
  }),
);

export default function Team() {
  const { user: currentUser } = useAuth();
  const { can } = usePermissions();
  const { showToast } = useGlobalToast();
  const [users, setUsers] = useState<TeamUser[]>([]);
  const [limits, setLimits] = useState<TeamLimits | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<TeamUser | null>(null);
  const [selectedPermissions, setSelectedPermissions] = useState<
    PermissionKey[]
  >([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingPermissions, setIsSavingPermissions] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    email: "",
    firstName: "",
    lastName: "",
    phone: "",
    password: "",
    role: "staff",
    isActive: true,
  });
  const [newPassword, setNewPassword] = useState("");

  const isOwner = currentUser?.role === "owner";
  const isAdmin = currentUser?.role === "admin" || isOwner;

  useEffect(() => {
    fetchTeamUsers();
  }, []);

  const fetchTeamUsers = async () => {
    try {
      setIsLoading(true);
      setLoadError(false);
      const response = await apiClient.get("/users/team");
      setUsers(response.data.data);
      setLimits(response.data.limits);
    } catch (err: unknown) {
      setLoadError(true);
      setUsers([]);
      const errorMessage =
        err instanceof Error && "response" in err
          ? (err as { response?: { data?: { message?: string } } }).response
              ?.data?.message
          : "Error al cargar el equipo";
      showToast(errorMessage || "Error al cargar el equipo", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      await apiClient.post("/users/team", formData);
      showToast("Usuario creado correctamente", "success");
      setShowCreateModal(false);
      resetForm();
      fetchTeamUsers();
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error && "response" in err
          ? (err as { response?: { data?: { message?: string } } }).response
              ?.data?.message
          : "Error al crear usuario";
      showToast(errorMessage || "Error al crear usuario", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    try {
      setIsSubmitting(true);
      await apiClient.put(`/users/team/${selectedUser.id}`, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        role: formData.role,
        isActive: formData.isActive,
      });
      showToast("Usuario actualizado correctamente", "success");
      setShowEditModal(false);
      setSelectedUser(null);
      fetchTeamUsers();
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error && "response" in err
          ? (err as { response?: { data?: { message?: string } } }).response
              ?.data?.message
          : "Error al actualizar usuario";
      showToast(errorMessage || "Error al actualizar usuario", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    try {
      setIsSubmitting(true);
      await apiClient.delete(`/users/team/${selectedUser.id}`);
      showToast("Usuario eliminado correctamente", "success");
      setShowDeleteModal(false);
      setSelectedUser(null);
      fetchTeamUsers();
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error && "response" in err
          ? (err as { response?: { data?: { message?: string } } }).response
              ?.data?.message
          : "Error al eliminar usuario";
      showToast(errorMessage || "Error al eliminar usuario", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    try {
      setIsSubmitting(true);
      await apiClient.put(`/users/team/${selectedUser.id}/password`, {
        newPassword,
      });
      showToast("Contraseña reseteada correctamente", "success");
      setShowPasswordModal(false);
      setSelectedUser(null);
      setNewPassword("");
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error && "response" in err
          ? (err as { response?: { data?: { message?: string } } }).response
              ?.data?.message
          : "Error al resetear contraseña";
      showToast(errorMessage || "Error al resetear contraseña", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditModal = (user: TeamUser) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      phone: user.phone || "",
      password: "",
      role: user.role,
      isActive: user.isActive,
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (user: TeamUser) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const openPasswordModal = (user: TeamUser) => {
    setSelectedUser(user);
    setNewPassword("");
    setShowPasswordModal(true);
  };

  const openPermissionsModal = (user: TeamUser) => {
    setSelectedUser(user);
    setSelectedPermissions(user.permissions ?? []);
    setShowPermissionsModal(true);
  };

  const togglePermission = (key: PermissionKey) => {
    setSelectedPermissions((prev) =>
      prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key],
    );
  };

  const handleSavePermissions = async () => {
    if (!selectedUser) return;
    try {
      setIsSavingPermissions(true);
      await apiClient.put(`/users/team/${selectedUser.id}/permissions`, {
        permissions: selectedPermissions,
      });
      showToast("Permisos actualizados correctamente", "success");
      setShowPermissionsModal(false);
      setSelectedUser(null);
      fetchTeamUsers();
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error && "response" in err
          ? (err as { response?: { data?: { message?: string } } }).response
              ?.data?.message
          : "Error al actualizar permisos";
      showToast(errorMessage || "Error al actualizar permisos", "error");
    } finally {
      setIsSavingPermissions(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: "",
      firstName: "",
      lastName: "",
      phone: "",
      password: "",
      role: "staff",
      isActive: true,
    });
  };

  const getRoleBadge = (role: string) => {
    const styles: Record<string, string> = {
      owner: "bg-purple-100 text-purple-800",
      admin: "bg-blue-100 text-blue-800",
      staff: "bg-gray-100 text-gray-800",
    };
    const labels: Record<string, string> = {
      owner: "Superadmin",
      admin: "Administrador",
      staff: "Empleado",
    };
    const icons: Record<string, React.ReactNode> = {
      owner: <ShieldCheck className="h-3 w-3 mr-1" />,
      admin: <Shield className="h-3 w-3 mr-1" />,
      staff: <User className="h-3 w-3 mr-1" />,
    };
    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${styles[role] || styles.staff}`}
      >
        {icons[role]}
        {labels[role] || role}
      </span>
    );
  };

  const canEditUser = (user: TeamUser) => {
    if (user.id === currentUser?.id) return false; // Can't edit yourself here
    if (user.role === "owner") return false; // Can't edit owner
    if (user.role === "admin" && !isOwner) return false; // Only owner can edit admin
    return isAdmin;
  };

  const canDeleteUser = (user: TeamUser) => {
    if (user.id === currentUser?.id) return false; // Can't delete yourself
    if (user.role === "owner") return false; // Can't delete owner
    if (user.role === "admin" && !isOwner) return false; // Only owner can delete admin
    return isAdmin;
  };

  const canAddUser = () => {
    if (!limits) return false;
    return limits.currentUsers < limits.maxUsers;
  };

  const handleResendInvitation = async (user: TeamUser) => {
    try {
      await apiClient.post(`/users/team/${user.id}/resend-invitation`);
      showToast(`Invitación reenviada a ${user.email}`, "success");
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error && "response" in err
          ? (err as { response?: { data?: { message?: string } } }).response
              ?.data?.message
          : "Error al reenviar invitación";
      showToast(errorMessage || "Error al reenviar invitación", "error");
    }
  };

  // Editor de permisos: solo tiene sentido para empleados (staff); los
  // admin/owner tienen acceso total. Cosmético — el backend es la barrera
  // real (`requirePermission("equipo:gestionar")` en cada endpoint).
  const canShowPermissionsButton = (user: TeamUser) =>
    can("equipo:gestionar") &&
    user.role === "staff" &&
    user.id !== currentUser?.id;

  const canShowFullAccessNote = (user: TeamUser) =>
    can("equipo:gestionar") && user.role === "admin";

  const isPendingInvitation = Boolean(
    selectedUser && !selectedUser.emailVerified,
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 md:gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">
            Mi Equipo
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            {limits
              ? `${limits.currentUsers} / ${limits.maxUsers} usuarios`
              : "Gestiona los usuarios que pueden acceder al sistema"}
          </p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setShowCreateModal(true);
          }}
          disabled={!canAddUser()}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm md:text-base"
        >
          <UserPlus className="h-4 w-4" />
          <span>Añadir Usuario</span>
        </Button>
      </div>

      {/* Limit Warning */}
      {limits && limits.currentUsers >= limits.maxUsers && (
        <div className="p-3 md:p-4 bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-xl flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 text-sm">
          <div className="flex items-center gap-2 flex-1">
            <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0" />
            <span>Has alcanzado el límite de usuarios de tu plan.</span>
          </div>
          <a
            href="/subscription"
            className="text-yellow-800 hover:text-yellow-900 underline font-medium"
          >
            Ver planes
          </a>
        </div>
      )}

      {/* Users List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loadError ? (
          <div className="text-center py-16 md:py-20 px-4">
            <AlertCircle className="h-12 w-12 md:h-16 md:w-16 mx-auto mb-4 text-red-300" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">
              No se pudo cargar el equipo
            </h3>
            <p className="text-gray-500 text-sm mb-4">
              Ha ocurrido un error al obtener los usuarios. Comprueba tu
              conexión e inténtalo de nuevo.
            </p>
            <Button variant="outline" onClick={() => fetchTeamUsers()}>
              Reintentar
            </Button>
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-16 md:py-20">
            <Users className="h-12 w-12 md:h-16 md:w-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">
              No hay usuarios en el equipo
            </h3>
            <p className="text-gray-500 text-sm">
              Añade usuarios para gestionar tu negocio
            </p>
          </div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="md:hidden divide-y divide-gray-100">
              {users.map((user) => (
                <div key={user.id} className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-primary-700 font-medium text-sm">
                          {user.firstName[0]}
                          {user.lastName[0]}
                        </span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-gray-900 truncate">
                          {user.firstName} {user.lastName}
                          {user.id === currentUser?.id && (
                            <span className="ml-2 text-xs text-gray-500">
                              (Tú)
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-600 truncate">
                          {user.email}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {!user.emailVerified && canEditUser(user) && (
                        <button
                          onClick={() => handleResendInvitation(user)}
                          className="p-2 text-gray-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg"
                          title="Reenviar invitación"
                        >
                          <Send className="h-4 w-4" />
                        </button>
                      )}
                      {canShowPermissionsButton(user) && (
                        <button
                          onClick={() => openPermissionsModal(user)}
                          className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg"
                          title="Permisos"
                        >
                          <Lock className="h-4 w-4" />
                        </button>
                      )}
                      {canEditUser(user) && (
                        <button
                          onClick={() => openEditModal(user)}
                          className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                      )}
                      {canDeleteUser(user) && (
                        <button
                          onClick={() => openDeleteModal(user)}
                          className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-2 ml-[3.25rem]">
                    {getRoleBadge(user.role)}
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        user.isActive
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {user.isActive ? "Activo" : "Inactivo"}
                    </span>
                    {!user.emailVerified && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                        <Clock className="h-3 w-3 mr-1" />
                        Pendiente
                      </span>
                    )}
                  </div>
                  {canShowFullAccessNote(user) && (
                    <p className="mt-1 ml-[3.25rem] text-xs text-gray-400">
                      Acceso total (administrador)
                    </p>
                  )}
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-600">
                      Usuario
                    </th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">
                      Contacto
                    </th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">
                      Rol
                    </th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">
                      Estado
                    </th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">
                      Último acceso
                    </th>
                    <th className="text-right py-3 px-4 font-medium text-gray-600">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr
                      key={user.id}
                      className="border-b border-gray-100 hover:bg-gray-50"
                    >
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                            <span className="text-primary-700 font-medium">
                              {user.firstName[0]}
                              {user.lastName[0]}
                            </span>
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">
                              {user.firstName} {user.lastName}
                              {user.id === currentUser?.id && (
                                <span className="ml-2 text-xs text-gray-500">
                                  (Tú)
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="space-y-1">
                          <div className="flex items-center text-sm text-gray-600">
                            <Mail className="h-3 w-3 mr-1" />
                            {user.email}
                          </div>
                          {user.phone && (
                            <div className="flex items-center text-sm text-gray-500">
                              <Phone className="h-3 w-3 mr-1" />
                              {user.phone}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        {getRoleBadge(user.role)}
                        {canShowFullAccessNote(user) && (
                          <p className="mt-1 text-xs text-gray-400">
                            Acceso total (administrador)
                          </p>
                        )}
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex flex-col gap-1">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium w-fit ${
                              user.isActive
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {user.isActive ? "Activo" : "Inactivo"}
                          </span>
                          {!user.emailVerified && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 w-fit">
                              <Clock className="h-3 w-3 mr-1" />
                              Pendiente
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-500">
                        {user.lastLoginAt
                          ? new Date(user.lastLoginAt).toLocaleDateString(
                              "es-ES",
                              {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              },
                            )
                          : "Nunca"}
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center justify-end gap-2">
                          {/* Resend invitation button - only for unverified users */}
                          {!user.emailVerified && canEditUser(user) && (
                            <button
                              onClick={() => handleResendInvitation(user)}
                              className="p-2 text-gray-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                              title="Reenviar invitación"
                            >
                              <Send className="h-4 w-4" />
                            </button>
                          )}
                          {canShowPermissionsButton(user) && (
                            <button
                              onClick={() => openPermissionsModal(user)}
                              className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                              title="Permisos"
                            >
                              <Lock className="h-4 w-4" />
                            </button>
                          )}
                          {canEditUser(user) && (
                            <>
                              <button
                                onClick={() => openEditModal(user)}
                                className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                                title="Editar"
                              >
                                <Edit2 className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => openPasswordModal(user)}
                                className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                title="Resetear contraseña"
                              >
                                <Key className="h-4 w-4" />
                              </button>
                            </>
                          )}
                          {canDeleteUser(user) && (
                            <button
                              onClick={() => openDeleteModal(user)}
                              className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Eliminar"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Añadir Usuario
            </h3>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre *
                  </label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) =>
                      setFormData({ ...formData, firstName: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Apellidos *
                  </label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) =>
                      setFormData({ ...formData, lastName: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Teléfono
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contraseña *
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  minLength={8}
                  placeholder="Mínimo 8 caracteres"
                  required
                />
              </div>
              {isOwner && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rol
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) =>
                      setFormData({ ...formData, role: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="staff">Empleado</option>
                    <option value="admin">Administrador</option>
                  </select>
                </div>
              )}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowCreateModal(false)}
                  disabled={isSubmitting}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  className="flex-1"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Creando...
                    </>
                  ) : (
                    "Crear Usuario"
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Editar Usuario
            </h3>
            <form onSubmit={handleUpdateUser} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre *
                  </label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) =>
                      setFormData({ ...formData, firstName: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Apellidos *
                  </label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) =>
                      setFormData({ ...formData, lastName: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  disabled
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  El email no se puede cambiar
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Teléfono
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              {isOwner && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rol
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) =>
                      setFormData({ ...formData, role: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="staff">Empleado</option>
                    <option value="admin">Administrador</option>
                  </select>
                </div>
              )}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData({ ...formData, isActive: e.target.checked })
                  }
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <label
                  htmlFor="isActive"
                  className="text-sm font-medium text-gray-700"
                >
                  Usuario activo
                </label>
              </div>
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedUser(null);
                  }}
                  disabled={isSubmitting}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  className="flex-1"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    "Guardar Cambios"
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete User Modal */}
      {showDeleteModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              {isPendingInvitation ? "Cancelar invitación" : "Eliminar Usuario"}
            </h3>
            <p className="text-gray-600 mb-6">
              {isPendingInvitation ? (
                <>
                  ¿Deseas cancelar la invitación pendiente de{" "}
                  <strong>
                    {selectedUser.firstName} {selectedUser.lastName}
                  </strong>
                  ? Esta acción no se puede deshacer.
                </>
              ) : (
                <>
                  ¿Estás seguro de que deseas eliminar a{" "}
                  <strong>
                    {selectedUser.firstName} {selectedUser.lastName}
                  </strong>
                  ? Esta acción no se puede deshacer.
                </>
              )}
            </p>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedUser(null);
                }}
                disabled={isSubmitting}
              >
                {isPendingInvitation ? "Volver" : "Cancelar"}
              </Button>
              <button
                type="button"
                onClick={handleDeleteUser}
                disabled={isSubmitting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin inline" />
                    {isPendingInvitation ? "Cancelando..." : "Eliminando..."}
                  </>
                ) : isPendingInvitation ? (
                  "Cancelar invitación"
                ) : (
                  "Eliminar"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showPasswordModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Resetear Contraseña
            </h3>
            <p className="text-gray-600 mb-4">
              Establecer nueva contraseña para{" "}
              <strong>
                {selectedUser.firstName} {selectedUser.lastName}
              </strong>
            </p>
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nueva Contraseña *
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  minLength={8}
                  placeholder="Mínimo 8 caracteres"
                  required
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setSelectedUser(null);
                    setNewPassword("");
                  }}
                  disabled={isSubmitting}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  className="flex-1"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    "Resetear Contraseña"
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Permissions Modal */}
      {showPermissionsModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full p-6 max-h-[90dvh] overflow-y-auto">
            <h3 className="text-xl font-bold text-gray-900 mb-1">
              Permisos de {selectedUser.firstName} {selectedUser.lastName}
            </h3>
            <p className="text-gray-500 text-sm mb-4">
              Elige qué puede hacer este empleado en el sistema.
            </p>

            {/* Plantillas rápidas */}
            <div className="flex flex-wrap gap-2 mb-5">
              {PERMISSION_PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => setSelectedPermissions(preset.keys)}
                  className="px-3 py-1.5 text-xs md:text-sm font-medium rounded-full border border-primary-200 text-primary-700 bg-primary-50 hover:bg-primary-100 transition-colors"
                >
                  {preset.label}
                </button>
              ))}
            </div>

            {/* Permisos agrupados */}
            <div className="space-y-5 mb-6">
              {GROUPED_PERMISSIONS.map((group) => (
                <div key={group.key}>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">
                    {group.label}
                  </h4>
                  <div className="space-y-2">
                    {group.permissions.map((perm) => (
                      <label
                        key={perm.key}
                        className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedPermissions.includes(perm.key)}
                          onChange={() => togglePermission(perm.key)}
                          className="mt-0.5 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <span>{perm.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-3 pt-4 border-t border-gray-100">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowPermissionsModal(false);
                  setSelectedUser(null);
                }}
                disabled={isSavingPermissions}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                variant="primary"
                className="flex-1"
                onClick={handleSavePermissions}
                disabled={isSavingPermissions}
              >
                {isSavingPermissions ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin inline" />
                    Guardando...
                  </>
                ) : (
                  "Guardar"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
